// src/components/pages/AR/ArcapsuleViewPage.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useGeolocationWatch } from "../../../hooks/useGeolocationWatch";
import { useDeviceOrientation } from "../../../hooks/useDeviceOrientation";
import {
  bearingDegrees,
  clampNumber,
  directionDeltaDegrees,
  haversineDistanceMeters,
} from "../../../utils/geo";
import {
  hasGetUserMedia,
  isMobileUserAgent,
  isSecureContextSafe,
} from "../../../utils/platform";
import { playArrivalPing, unlockArAudio } from "../../../services/arAudioService";

/**
 * AR SIZE TUNING
 */
const AR_SIZE_CONFIG = {
  NEAR_METERS: 12,
  FAR_METERS: 450,
  MIN_ICON_PX: 25,
  MAX_ICON_PX: 220,
  EASE_POWER: 2.6,
};

/**
 * HEADING SMOOTHING (helps Android a lot, but not always enough alone)
 */
const HEADING_SMOOTHING = {
  ENABLED: true,
  EMA_ALPHA: 0.16,
  MAX_DEG_PER_UPDATE: 25,
};

/**
 * UI STABILIZATION (targets Android left/right jitter)
 *
 * - LEFT_DEADBAND_PERCENT: ignore tiny left/right changes smaller than this.
 * - LEFT_EMA_ALPHA: smoothing factor for displayed position, smaller = smoother.
 * - LEFT_MAX_STEP_PERCENT: clamp how fast the displayed value can move per update.
 * - ON_SCREEN_HYSTERESIS_DEG: prevents edge flicker; larger = more stable.
 */
const AR_UI_SMOOTHING = {
  ENABLED: true,
  LEFT_DEADBAND_PERCENT: 1.2,
  LEFT_EMA_ALPHA: 0.14,
  LEFT_MAX_STEP_PERCENT: 6,
  ON_SCREEN_HYSTERESIS_DEG: 6,
};

function parseNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatMeters(meters) {
  if (typeof meters !== "number" || !Number.isFinite(meters)) return "";
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = meters / 1000;
  return `${km.toFixed(km < 10 ? 2 : 1)} km`;
}

function iconSizeFromDistance(distanceMeters, cfg) {
  const { NEAR_METERS, FAR_METERS, MIN_ICON_PX, MAX_ICON_PX, EASE_POWER } = cfg;

  if (typeof distanceMeters !== "number" || !Number.isFinite(distanceMeters)) {
    return MAX_ICON_PX;
  }

  const far = Math.max(NEAR_METERS + 1, FAR_METERS);
  const near = Math.max(0, NEAR_METERS);

  const tRaw = (far - distanceMeters) / (far - near);
  const t = clampNumber(tRaw, 0, 1);

  const eased = Math.pow(t, Math.max(1, EASE_POWER));

  const px = MIN_ICON_PX + (MAX_ICON_PX - MIN_ICON_PX) * eased;
  return clampNumber(px, MIN_ICON_PX, MAX_ICON_PX);
}

function norm360(deg) {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

function shortestDeltaDeg(fromDeg, toDeg) {
  const a = norm360(fromDeg);
  const b = norm360(toDeg);
  let d = b - a;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
}

function smoothHeading(nextHeading, prevHeading, cfg) {
  if (typeof nextHeading !== "number" || !Number.isFinite(nextHeading)) return prevHeading;
  if (typeof prevHeading !== "number" || !Number.isFinite(prevHeading)) return norm360(nextHeading);

  const delta = shortestDeltaDeg(prevHeading, nextHeading);

  const clampedDelta = clampNumber(
    delta,
    -Math.abs(cfg.MAX_DEG_PER_UPDATE),
    Math.abs(cfg.MAX_DEG_PER_UPDATE)
  );

  const blended = prevHeading + clampedDelta * cfg.EMA_ALPHA;
  return norm360(blended);
}

export default function ArcapsuleViewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const state = location && location.state ? location.state : null;

  const capsuleId =
    state && typeof state.capsuleId === "string" ? state.capsuleId : "";
  const targetLat = parseNumber(state && state.targetLat);
  const targetLng = parseNumber(state && state.targetLng);
  const capsuleType =
    state && typeof state.capsuleType === "string"
      ? state.capsuleType
      : "default";
  const iconUrl =
    state && typeof state.iconUrl === "string" ? state.iconUrl : "";
  const title =
    state && typeof state.title === "string" ? state.title : "Time Capsule";

  const [cameraStatus, setCameraStatus] = useState("idle");
  const [cameraError, setCameraError] = useState("");
  const [hasUserStarted, setHasUserStarted] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const [arrived, setArrived] = useState(false);
  const arrivedRef = useRef(false);

  const mobileOk = isMobileUserAgent();
  const secureOk = isSecureContextSafe();

  const targetOk =
    typeof targetLat === "number" &&
    Number.isFinite(targetLat) &&
    typeof targetLng === "number" &&
    Number.isFinite(targetLng);

  const canUseCamera = secureOk && hasGetUserMedia();

  const { position, status: geoStatus, error: geoError } = useGeolocationWatch({
    enabled: hasUserStarted,
    highAccuracy: true,
    timeoutMs: 15000,
    maximumAgeMs: 1000,
    throttleMs: 200,
  });

  const {
    heading,
    status: compassStatus,
    error: compassError,
    requestPermission: requestCompassPermission,
  } = useDeviceOrientation({
    enabled: hasUserStarted,
    throttleMs: 50,
  });

  // Smooth heading for Android jitter
  const smoothedHeadingRef = useRef(null);
  const [headingSmooth, setHeadingSmooth] = useState(null);

  useEffect(() => {
    if (!hasUserStarted) return;

    if (!HEADING_SMOOTHING.ENABLED) {
      setHeadingSmooth(
        typeof heading === "number" && Number.isFinite(heading) ? norm360(heading) : null
      );
      return;
    }

    if (typeof heading !== "number" || !Number.isFinite(heading)) return;

    const prev = smoothedHeadingRef.current;
    const next = smoothHeading(heading, prev, HEADING_SMOOTHING);

    smoothedHeadingRef.current = next;
    setHeadingSmooth(next);
  }, [heading, hasUserStarted]);

  const targetPoint = useMemo(() => {
    if (!targetOk) return null;
    return { latitude: targetLat, longitude: targetLng };
  }, [targetLat, targetLng, targetOk]);

  const userPoint = useMemo(() => {
    if (!position) return null;
    return { latitude: position.latitude, longitude: position.longitude };
  }, [position]);

  const distanceMeters = useMemo(() => {
    if (!userPoint || !targetPoint) return null;
    return haversineDistanceMeters(userPoint, targetPoint);
  }, [userPoint, targetPoint]);

  const targetBearing = useMemo(() => {
    if (!userPoint || !targetPoint) return null;
    return bearingDegrees(userPoint, targetPoint);
  }, [userPoint, targetPoint]);

  const usedHeading = useMemo(() => {
    if (typeof headingSmooth === "number" && Number.isFinite(headingSmooth)) return headingSmooth;
    if (typeof heading === "number" && Number.isFinite(heading)) return norm360(heading);
    return null;
  }, [heading, headingSmooth]);

  const deltaDeg = useMemo(() => {
    if (typeof usedHeading !== "number" || !Number.isFinite(usedHeading)) return null;
    if (typeof targetBearing !== "number" || !Number.isFinite(targetBearing)) return null;
    return directionDeltaDegrees(usedHeading, targetBearing);
  }, [usedHeading, targetBearing]);

  const arrivalRadiusMeters = 12;

  // Forward cone; icon only shows when inside.
  const fovDegrees = 60;
  const halfFov = fovDegrees / 2;

  // Hysteresis for onScreen so it doesn't flicker as heading jitters at the edge.
  const [onScreenStable, setOnScreenStable] = useState(false);
  const onScreenStableRef = useRef(false);

  useEffect(() => {
    if (!hasUserStarted) {
      onScreenStableRef.current = false;
      setOnScreenStable(false);
      return;
    }

    if (typeof deltaDeg !== "number" || !Number.isFinite(deltaDeg)) {
      onScreenStableRef.current = false;
      setOnScreenStable(false);
      return;
    }

    const enterDeg = halfFov;
    const exitDeg = halfFov + Math.max(0, AR_UI_SMOOTHING.ON_SCREEN_HYSTERESIS_DEG);

    const abs = Math.abs(deltaDeg);
    const prev = onScreenStableRef.current;

    let next = prev;
    if (!prev) {
      // Only enter when clearly inside the normal half-FOV
      next = abs <= enterDeg;
    } else {
      // Only exit when clearly outside a slightly larger boundary
      next = abs <= exitDeg;
    }

    onScreenStableRef.current = next;
    setOnScreenStable(next);
  }, [deltaDeg, hasUserStarted, halfFov]);

  // Compute the target horizontal position (unsmoothed).
  const iconLeftTargetPercent = useMemo(() => {
    if (typeof deltaDeg !== "number" || !Number.isFinite(deltaDeg)) return 50;

    const norm = clampNumber(deltaDeg / halfFov, -1, 1); // -1..1
    const x = 50 + norm * 40;

    return clampNumber(x, 10, 90);
  }, [deltaDeg, halfFov]);

  // Smooth the rendered horizontal position to kill Android jitter.
  const iconLeftSmoothRef = useRef(50);
  const [iconLeftPercent, setIconLeftPercent] = useState(50);

  useEffect(() => {
    if (!hasUserStarted) {
      iconLeftSmoothRef.current = 50;
      setIconLeftPercent(50);
      return;
    }

    if (!AR_UI_SMOOTHING.ENABLED) {
      iconLeftSmoothRef.current = iconLeftTargetPercent;
      setIconLeftPercent(iconLeftTargetPercent);
      return;
    }

    const prev = iconLeftSmoothRef.current;
    const target = iconLeftTargetPercent;

    const diff = target - prev;

    // Deadband to ignore micro jitter
    if (Math.abs(diff) < AR_UI_SMOOTHING.LEFT_DEADBAND_PERCENT) {
      setIconLeftPercent(prev);
      return;
    }

    // EMA step toward target, with a max-step clamp
    const step = diff * AR_UI_SMOOTHING.LEFT_EMA_ALPHA;
    const clampedStep = clampNumber(
      step,
      -Math.abs(AR_UI_SMOOTHING.LEFT_MAX_STEP_PERCENT),
      Math.abs(AR_UI_SMOOTHING.LEFT_MAX_STEP_PERCENT)
    );

    const next = clampNumber(prev + clampedStep, 10, 90);

    iconLeftSmoothRef.current = next;
    setIconLeftPercent(next);
  }, [hasUserStarted, iconLeftTargetPercent]);

  const iconSizePx = useMemo(() => {
    return iconSizeFromDistance(distanceMeters, AR_SIZE_CONFIG);
  }, [distanceMeters]);

  const showLeftArrow = useMemo(() => {
    if (typeof deltaDeg !== "number" || !Number.isFinite(deltaDeg)) return false;
    return !onScreenStable && deltaDeg < 0;
  }, [deltaDeg, onScreenStable]);

  const showRightArrow = useMemo(() => {
    if (typeof deltaDeg !== "number" || !Number.isFinite(deltaDeg)) return false;
    return !onScreenStable && deltaDeg > 0;
  }, [deltaDeg, onScreenStable]);

  useEffect(() => {
    if (arrivedRef.current) return;
    if (typeof distanceMeters !== "number" || !Number.isFinite(distanceMeters)) return;

    if (distanceMeters <= arrivalRadiusMeters) {
      arrivedRef.current = true;
      setArrived(true);
      playArrivalPing();
    }
  }, [distanceMeters]);

  const stopCamera = useCallback(() => {
    const stream = streamRef.current;
    streamRef.current = null;

    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
      } catch {}
    }

    if (stream) {
      try {
        const tracks = stream.getTracks();
        tracks.forEach((t) => {
          try {
            t.stop();
          } catch {}
        });
      } catch {}
    }

    setCameraStatus("idle");
  }, []);

  const startCamera = useCallback(async () => {
    if (!canUseCamera) {
      setCameraStatus("unsupported");
      setCameraError(
        !secureOk ? "Camera requires HTTPS." : "Camera is not supported on this device."
      );
      return;
    }

    setCameraStatus("starting");
    setCameraError("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch {}
      }

      setCameraStatus("running");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Camera permission denied";
      setCameraStatus("denied");
      setCameraError(msg);
    }
  }, [canUseCamera, secureOk]);

  const onStart = useCallback(async () => {
    setHasUserStarted(true);

    const res = await unlockArAudio();
    setAudioUnlocked(Boolean(res && res.ok));

    await startCamera();
    await requestCompassPermission();
  }, [requestCompassPermission, startCamera]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const fallbackIconLabel = useMemo(() => {
    if (capsuleType === "memberExclusive") return "Treasure";
    if (capsuleType === "sponsored") return "Sponsored";
    return "Capsule";
  }, [capsuleType]);

  const hasIcon = Boolean(iconUrl);

  const topStatusLine = useMemo(() => {
    const parts = [];

    if (geoStatus === "unsupported") parts.push("Location unsupported");
    else if (geoStatus === "error") parts.push("Location error");
    else if (geoStatus === "watching") parts.push("Location tracking");
    else parts.push("Location off");

    if (compassStatus === "unsupported") parts.push("Compass unsupported");
    else if (compassStatus === "denied") parts.push("Compass blocked");
    else if (compassStatus === "error") parts.push("Compass error");
    else if (compassStatus === "listening") parts.push("Compass enabled");
    else parts.push("Compass off");

    if (cameraStatus === "running") parts.push("Camera active");
    else if (cameraStatus === "starting") parts.push("Camera starting");
    else if (cameraStatus === "denied") parts.push("Camera blocked");
    else if (cameraStatus === "unsupported") parts.push("Camera unsupported");
    else parts.push("Camera off");

    return parts.join(" • ");
  }, [cameraStatus, compassStatus, geoStatus]);

  const sensorChipForStatus = useCallback((status, labels) => {
    const { ok, warn, err, off } = labels;
    if (status === "unsupported") return { label: err, tone: "error" };
    if (status === "denied") return { label: err, tone: "error" };
    if (status === "error") return { label: err, tone: "error" };
    if (status === "starting") return { label: warn, tone: "warn" };
    if (status === "watching") return { label: ok, tone: "ok" };
    if (status === "listening") return { label: ok, tone: "ok" };
    if (status === "running") return { label: ok, tone: "ok" };
    return { label: off, tone: "off" };
  }, []);

  const sensorChips = useMemo(() => {
    return [
      {
        id: "location",
        name: "Location",
        ...sensorChipForStatus(geoStatus, {
          ok: "Tracking",
          warn: "Starting",
          err: "Blocked",
          off: "Off",
        }),
      },
      {
        id: "compass",
        name: "Compass",
        ...sensorChipForStatus(compassStatus, {
          ok: "Enabled",
          warn: "Starting",
          err: "Blocked",
          off: "Off",
        }),
      },
      {
        id: "camera",
        name: "Camera",
        ...sensorChipForStatus(cameraStatus, {
          ok: "Active",
          warn: "Starting",
          err: "Blocked",
          off: "Off",
        }),
      },
    ];
  }, [cameraStatus, compassStatus, geoStatus, sensorChipForStatus]);

  const distanceText = useMemo(() => {
    if (!hasUserStarted) return "";
    if (typeof distanceMeters !== "number" || !Number.isFinite(distanceMeters)) return "Distance: calculating…";
    return `Distance: ${formatMeters(distanceMeters)}`;
  }, [distanceMeters, hasUserStarted]);

  const accuracyText = useMemo(() => {
    if (!hasUserStarted) return "";
    if (!position || typeof position.accuracy !== "number" || !Number.isFinite(position.accuracy)) return "";
    return `GPS accuracy: ±${Math.round(position.accuracy)} m`;
  }, [hasUserStarted, position]);

  const guidanceText = useMemo(() => {
    if (!hasUserStarted) return "Tap Start to begin.";
    if (!targetOk) return "Missing capsule location.";
    if (arrived) return "You’ve arrived.";

    if (typeof deltaDeg !== "number" || !Number.isFinite(deltaDeg)) {
      return "Move your phone to calibrate, then point toward the capsule.";
    }

    if (onScreenStable) return "Walk forward, keep the icon centered.";
    return deltaDeg < 0 ? "Turn left until the icon appears." : "Turn right until the icon appears.";
  }, [arrived, deltaDeg, hasUserStarted, onScreenStable, targetOk]);

  const compassButtonLabel = useMemo(() => {
    if (compassStatus === "listening") return "Compass Enabled";
    if (compassStatus === "denied") return "Compass Blocked";
    if (compassStatus === "unsupported") return "Compass Unsupported";
    if (compassStatus === "error") return "Retry Compass";
    return "Enable Compass";
  }, [compassStatus]);

  if (!mobileOk) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Camera Guide is mobile-only
        </Typography>
        <Typography sx={{ opacity: 0.8, mb: 2 }}>
          Open this feature on your phone to use the camera and compass.
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)} sx={{ borderRadius: "50px", textTransform: "none" }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!targetOk) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Missing capsule location
        </Typography>
        <Typography sx={{ opacity: 0.8, mb: 2 }}>
          This AR view needs a target GPS location.
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)} sx={{ borderRadius: "50px", textTransform: "none" }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const pulseKeyframes = {
    "0%": { transform: "scale(1)", opacity: 0.65 },
    "50%": { transform: "scale(1.15)", opacity: 1 },
    "100%": { transform: "scale(1)", opacity: 0.65 },
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: theme.palette.common.black, overflow: "hidden" }}>
      <Box
        component="video"
        ref={videoRef}
        autoPlay
        muted
        playsInline
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          filter: arrived ? "brightness(0.85) saturate(1.1)" : "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          px: 2,
          pt: 1.5,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          background: `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0.65)}, ${alpha(theme.palette.common.black, 0)})`,
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="contained"
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.common.white, 0.14),
          }}
        >
          Back
        </Button>

        <Box sx={{ minWidth: 0, textAlign: "right" }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.common.white,
              fontWeight: 800,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "70vw",
              ml: "auto",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mt: 0.35 }}>
            <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.78) }}>
              {topStatusLine}
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 0.6,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 0.6,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.common.white, 0.65),
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 800,
                fontSize: 10,
              }}
            >
              Sensors
            </Typography>
            {sensorChips.map((chip) => {
              const tone =
                chip.tone === "ok"
                  ? {
                      dot: theme.palette.success.main,
                      bg: alpha(theme.palette.success.main, 0.12),
                      border: alpha(theme.palette.success.main, 0.45),
                    }
                  : chip.tone === "warn"
                  ? {
                      dot: theme.palette.warning.main,
                      bg: alpha(theme.palette.warning.main, 0.12),
                      border: alpha(theme.palette.warning.main, 0.5),
                    }
                  : chip.tone === "error"
                  ? {
                      dot: theme.palette.error.main,
                      bg: alpha(theme.palette.error.main, 0.12),
                      border: alpha(theme.palette.error.main, 0.5),
                    }
                  : {
                      dot: alpha(theme.palette.common.white, 0.5),
                      bg: alpha(theme.palette.common.white, 0.08),
                      border: alpha(theme.palette.common.white, 0.22),
                    };

              return (
                <Box
                  key={chip.id}
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: "999px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    bgcolor: tone.bg,
                    border: `1px solid ${tone.border}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: tone.dot,
                      boxShadow: `0 0 8px ${tone.dot}`,
                    }}
                  />
                  <Typography sx={{ color: theme.palette.common.white, fontSize: 11, lineHeight: 1 }}>
                    {chip.name}: {chip.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {hasUserStarted && !arrived && (showLeftArrow || showRightArrow) && (
        <>
          {showLeftArrow && (
            <Box
              sx={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                width: 56,
                height: 56,
                borderRadius: "999px",
                bgcolor: alpha(theme.palette.common.white, 0.16),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "arPulse 1.05s ease-in-out infinite",
                "@keyframes arPulse": pulseKeyframes,
              }}
            >
              <Typography sx={{ color: theme.palette.common.white, fontSize: 28, lineHeight: 1 }}>‹</Typography>
            </Box>
          )}
          {showRightArrow && (
            <Box
              sx={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                width: 56,
                height: 56,
                borderRadius: "999px",
                bgcolor: alpha(theme.palette.common.white, 0.16),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "arPulse 1.05s ease-in-out infinite",
                "@keyframes arPulse": pulseKeyframes,
              }}
            >
              <Typography sx={{ color: theme.palette.common.white, fontSize: 28, lineHeight: 1 }}>›</Typography>
            </Box>
          )}
        </>
      )}

      {hasUserStarted && onScreenStable && (
        <Box
          sx={{
            position: "absolute",
            top: "45%",
            left: `${iconLeftPercent}%`,
            transform: "translate(-50%, -50%)",
            width: iconSizePx,
            height: iconSizePx,
            borderRadius: "999px",
            bgcolor: arrived ? alpha(theme.palette.common.white, 0.22) : alpha(theme.palette.common.white, 0.14),
            border: arrived
              ? `2px solid ${alpha(theme.palette.common.white, 0.55)}`
              : `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
            boxShadow: arrived
              ? `0 0 28px ${alpha(theme.palette.common.white, 0.35)}`
              : `0 0 20px ${alpha(theme.palette.common.black, 0.35)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            transition:
              "left 80ms linear, width 180ms ease, height 180ms ease, border 180ms ease",
          }}
        >
          {hasIcon ? (
            <Box
              component="img"
              src={iconUrl}
              alt=""
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                pointerEvents: "none",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.common.white, 0.08),
              }}
            >
              <Typography sx={{ color: theme.palette.common.white, fontWeight: 800, fontSize: 14 }}>
                {" "}
                {fallbackIconLabel}{" "}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {hasUserStarted && arrived && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.8,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.common.black, 0.55),
              border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
              textAlign: "center",
              maxWidth: "86vw",
            }}
          >
            <Typography sx={{ color: theme.palette.common.white, fontWeight: 900, fontSize: 22, mb: 0.5 }}>
              You’ve arrived
            </Typography>
            <Typography sx={{ color: alpha(theme.palette.common.white, 0.85), fontSize: 14 }}>
              You’re at the capsule location.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          px: 2,
          pt: 2,
          pb: 2.5,
          background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.75)}, ${alpha(theme.palette.common.black, 0)})`,
        }}
      >
        <Box
          sx={{
            mx: "auto",
            maxWidth: 520,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.common.black, 0.48),
            border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
            p: 2,
          }}
        >
          <Typography sx={{ color: theme.palette.common.white, fontWeight: 800, mb: 0.75 }}>
            {guidanceText}
          </Typography>

          {distanceText && (
            <Typography sx={{ color: alpha(theme.palette.common.white, 0.82), fontSize: 13 }}>
              {distanceText}
            </Typography>
          )}
          {accuracyText && (
            <Typography sx={{ color: alpha(theme.palette.common.white, 0.72), fontSize: 13 }}>
              {accuracyText}
            </Typography>
          )}

          {(geoError || compassError || cameraError) && (
            <Box sx={{ mt: 1 }}>
              {geoError && (
                <Typography sx={{ color: alpha(theme.palette.error.light, 0.95), fontSize: 12 }}>
                  Location: {geoError.message}
                </Typography>
              )}
              {compassError && (
                <Typography sx={{ color: alpha(theme.palette.error.light, 0.95), fontSize: 12 }}>
                  Compass: {compassError}
                </Typography>
              )}
              {cameraError && (
                <Typography sx={{ color: alpha(theme.palette.error.light, 0.95), fontSize: 12 }}>
                  Camera: {cameraError}
                </Typography>
              )}
            </Box>
          )}

          {!hasUserStarted ? (
            <Button
              fullWidth
              variant="contained"
              onClick={onStart}
              sx={{
                mt: 1.5,
              }}
            >
              Start
            </Button>
          ) : (
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={async () => {
                  await startCamera();
                }}
                sx={{
                  borderColor: alpha(theme.palette.common.white, 0.35),
                  color: theme.palette.common.white,
                  "&:hover": { borderColor: alpha(theme.palette.common.white, 0.55) },
                }}
              >
                Retry Camera
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={async () => {
                  await requestCompassPermission();
                }}
                disabled={compassStatus === "listening"}
                sx={{
                  borderColor: alpha(theme.palette.common.white, 0.35),
                  color: theme.palette.common.white,
                  "&:hover": { borderColor: alpha(theme.palette.common.white, 0.55) },
                  "&.Mui-disabled": {
                    color: alpha(theme.palette.common.white, 0.6),
                    borderColor: alpha(theme.palette.common.white, 0.2),
                  },
                }}
              >
                {compassButtonLabel}
              </Button>
            </Box>
          )}

          {hasUserStarted && !audioUnlocked && (
            <Typography sx={{ color: alpha(theme.palette.common.white, 0.65), fontSize: 12, mt: 1 }}>
              Sound may be disabled by your browser until you interact.
            </Typography>
          )}

          {capsuleId ? (
            <Typography sx={{ color: alpha(theme.palette.common.white, 0.45), fontSize: 11, mt: 1 }}>
              capsuleId: {capsuleId}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
